/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  createEmptyStateResource,
  createErrorStateResource,
  createStateResource,
  HttpError,
  StateResource,
} from '@common/tech-shared';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { getUrl, ResourceUri } from '@ngxp/rest';
import { CommandOrder, CommandResource, CreateCommandActionProps } from '../command.model';
import {
  CommandActions,
  CreateCommandFailureProps,
  CreateCommandSuccessProps,
  UpdateCreateCommandProps,
} from './command.actions';

export const COMMAND_FEATURE_KEY = 'CommandState';

export interface CommandPartialState {
  readonly [COMMAND_FEATURE_KEY]: CommandState;
}

export interface CommandState {
  commandMap: { [key: string]: StateResource<CommandResource> };
}

export const initialState: CommandState = {
  commandMap: {},
};

const commandReducer: ActionReducer<CommandState, Action> = createReducer(
  initialState,
  on(
    CommandActions.createCommand,
    (state: CommandState, props: CreateCommandActionProps): CommandState => ({
      ...state,
      commandMap: {
        ...state.commandMap,
        [createCommandStateKey(props)]: createEmptyStateResource(true),
      },
    }),
  ),
  on(
    CommandActions.updateCreateCommand,
    (state: CommandState, props: UpdateCreateCommandProps): CommandState => ({
      ...state,
      commandMap: {
        ...state.commandMap,
        [createCommandStateKey(props.createCommandActionProps)]: createStateResource(props.command, true),
      },
    }),
  ),
  on(
    CommandActions.createCommandSuccess,
    (state: CommandState, props: CreateCommandSuccessProps): CommandState => ({
      ...state,
      commandMap: {
        ...state.commandMap,
        [createCommandStateKey(props.createCommandActionProps)]: createStateResource(props.command),
      },
    }),
  ),
  on(
    CommandActions.createCommandFailure,
    (state: CommandState, props: CreateCommandFailureProps): CommandState => ({
      ...state,
      commandMap: {
        ...state.commandMap,
        [createCommandStateKey(props.createCommandActionProps)]: createErrorStateResource(props.error as HttpError),
      },
    }),
  ),
);

export function reducer(state: CommandState, action: Action): CommandState {
  return commandReducer(state, action);
}

function createCommandStateKey(props: CreateCommandActionProps): string {
  return buildCommandStateKey(props.command.order, getUrl(props.resource, props.linkRel));
}

export function buildCommandStateKey(order: CommandOrder, uri: ResourceUri): string {
  return `${order}_${uri}`;
}
